import type { ProgressPercentage } from "@/models/course/Progress";

export function buildCourseHref(
  courseId: string | number,
  miniAppUuid?: string | null,
) {
  const coursePath = `/course/${courseId}`;

  if (!miniAppUuid) {
    return coursePath;
  }

  return `${coursePath}?miniAppUuid=${encodeURIComponent(miniAppUuid)}`;
}

const clampPercent = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

/**
 * `progress_percentage` changed from a plain number to an object (empty `{}`
 * when there is no progress). This reads any of the shapes we might receive —
 * legacy number, an object carrying a numeric field, or a lesson→percent map
 * (averaged) — and returns a single 0–100 value, or `null` when there is no
 * usable progress so callers can fall back.
 */
export function normalizeProgressPercentage(
  value: ProgressPercentage | undefined,
): number | null {
  if (typeof value === "number") {
    return Number.isNaN(value) ? null : clampPercent(value);
  }

  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;

    for (const key of ["percentage", "percent", "progress", "value"]) {
      const direct = obj[key];
      if (typeof direct === "number" && !Number.isNaN(direct)) {
        return clampPercent(direct);
      }
    }

    const nums = Object.values(obj).filter(
      (v): v is number => typeof v === "number" && !Number.isNaN(v),
    );
    if (nums.length > 0) {
      return clampPercent(nums.reduce((a, b) => a + b, 0) / nums.length);
    }
  }

  return null;
}
